(() => {
  'use strict';

  const CONFIG = {
    API_URL: (window.KONNY_THUMBNAIL_RULES && window.KONNY_THUMBNAIL_RULES.API_URL) || '',
    REGION: (window.KONNY_THUMBNAIL_RULES && window.KONNY_THUMBNAIL_RULES.REGION) || '',

    USE_CACHE: false,
    CACHE_TTL: 1000 * 60 * 5,
    CACHE_KEY_PREFIX: 'konny_thumbnail_rules_global_v5_temp',

    OBSERVER_DEBOUNCE: 120,
    SCROLL_SCAN_DEBOUNCE: 200,
    RESCAN_INTERVAL: 800,
    RESCAN_DURATION: 10000,

    SELECTORS: {
      productItem: '.product-item',
      productLink: '.product-item__image-link[href]',
      secondImage: '.product-item__image--two, .product-item__image:nth-of-type(2)',
      productList: '.product-list, .collection-product-list, .prdList, main, body'
    }
  };

  if (!CONFIG.API_URL) {
    console.warn('[Konny Thumbnail Rules] API_URL is missing.');
    return;
  }

  let rootObserver = null;
  let scanTimer = null;
  let scrollScanTimer = null;
  let matchedHandles = null;
  let periodicScanTimer = null;
  let periodicScanStopTimer = null;

  function normalizeText(value) {
    return String(value || '')
      .replace(/\u00A0/g, ' ')
      .trim()
      .replace(/^\/+|\/+$/g, '')
      .toLowerCase();
  }

  function decodePath(path) {
    try {
      return decodeURIComponent(path || '');
    } catch (e) {
      return path || '';
    }
  }

  function normalizePath(path) {
    return decodePath(path || '')
      .split('?')[0]
      .split('#')[0]
      .replace(/\/+$/, '')
      .toLowerCase();
  }

  function isTouchDevice() {
    return (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      window.matchMedia('(hover: none)').matches
    );
  }

  function getRegion() {
    const explicit = normalizeText(CONFIG.REGION);
    if (explicit) return explicit.toUpperCase();

    const host = location.hostname.toLowerCase();
    if (host.includes('.jp')) return 'JP';
    if (host.includes('.tw')) return 'GC';
    if (host.includes('.com')) return 'NA';

    return '';
  }

  function getCurrentPageContext() {
    const pathname = normalizePath(location.pathname);
    const segments = pathname.split('/').filter(Boolean);

    if (!segments.length) {
      return {
        pageType: '',
        category: '',
        pathname
      };
    }

    const first = segments[0];
    let pageType = '';

    switch (first) {
      case 'collections':
        pageType = 'collection';
        break;
      case 'pages':
        pageType = 'page';
        break;
      case 'blogs':
        pageType = 'blog';
        break;
      case 'search':
        pageType = 'search';
        break;
      default:
        pageType = first.replace(/s$/, '');
        break;
    }

    let category = '';

    if (pageType === 'collection' || pageType === 'page' || pageType === 'blog') {
      category = segments[1] || '';
    } else if (pageType === 'search') {
      category = 'search';
    }

    return {
      pageType: normalizeText(pageType),
      category: normalizeText(category),
      pathname
    };
  }

  function getProductHandleFromHref(href) {
    if (!href) return '';

    try {
      const url = new URL(href, location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/i);
      return match ? normalizeText(decodePath(match[1])) : '';
    } catch (e) {
      const match = String(href).match(/\/products\/([^/?#]+)/i);
      return match ? normalizeText(decodePath(match[1])) : '';
    }
  }

  function isEnabledValue(value) {
    const normalized = String(value || '')
      .replace(/\u00A0/g, ' ')
      .trim()
      .toUpperCase();

    return ['Y', 'YES', 'TRUE', '1', 'ON'].includes(normalized);
  }

  function getCacheKey(region) {
    return `${CONFIG.CACHE_KEY_PREFIX}:${region}`;
  }

  function getCachedRules(region) {
    if (!CONFIG.USE_CACHE) return null;

    try {
      const raw = localStorage.getItem(getCacheKey(region));
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.rules)) return null;
      if (Date.now() - parsed.ts > CONFIG.CACHE_TTL) return null;

      return parsed.rules;
    } catch (e) {
      return null;
    }
  }

  function setCachedRules(region, rules) {
    if (!CONFIG.USE_CACHE) return;

    try {
      localStorage.setItem(
        getCacheKey(region),
        JSON.stringify({
          ts: Date.now(),
          rules: Array.isArray(rules) ? rules : []
        })
      );
    } catch (e) {}
  }

  async function fetchRulesFromApi(region) {
    const url = new URL(CONFIG.API_URL);
    url.searchParams.set('region', region);

    const res = await fetch(url.toString(), {
      method: 'GET',
      credentials: 'omit',
      cache: 'no-store'
    });

    if (!res.ok) {
      throw new Error(`Failed to fetch rules: ${res.status}`);
    }

    const json = await res.json();
    const rules = Array.isArray(json.rules)
      ? json.rules
      : Array.isArray(json.items)
        ? json.items
        : [];

    setCachedRules(region, rules);
    return rules;
  }

  async function refreshRulesInBackground(region) {
    if (!CONFIG.USE_CACHE) return;

    try {
      await fetchRulesFromApi(region);
    } catch (e) {}
  }

  async function fetchRules(region) {
    const cached = getCachedRules(region);

    if (cached) {
      refreshRulesInBackground(region);
      return cached;
    }

    return fetchRulesFromApi(region);
  }

  function getMatchingHandles(rules, context) {
    const handleSet = new Set();

    rules.forEach((rule) => {
      const pageType = normalizeText(rule.page_type);
      const category = normalizeText(rule.category);
      const product = normalizeText(rule.product);
      const enabled = isEnabledValue(rule.enabled);

      if (!enabled) return;
      if (!pageType || !category || !product) return;
      if (pageType !== context.pageType) return;
      if (category !== context.category) return;

      handleSet.add(product);
    });

    return handleSet;
  }

  function getSecondImage(link) {
    if (!link || !link.querySelector) return null;
    return link.querySelector(CONFIG.SELECTORS.secondImage);
  }

  function setSecondImageActive(link, shouldActive) {
    const imgTwo = getSecondImage(link);
    if (!imgTwo) return;

    if (shouldActive) {
      imgTwo.classList.add('active');

      if (isTouchDevice()) {
        imgTwo.style.display = 'block';
      }
    } else {
      imgTwo.classList.remove('active');

      if (isTouchDevice()) {
        imgTwo.style.display = '';
      }
    }
  }

  function syncDesiredState(link, shouldActive) {
    setSecondImageActive(link, shouldActive);

    requestAnimationFrame(() => {
      setSecondImageActive(link, shouldActive);

      setTimeout(() => {
        setSecondImageActive(link, shouldActive);
      }, 0);
    });
  }

  function bindThumbnailRule(link) {
    if (!link || !link.isConnected) return;
    if (!matchedHandles || !matchedHandles.size) return;

    const href = link.getAttribute('href') || '';
    const handle = getProductHandleFromHref(href);
    if (!handle || !matchedHandles.has(handle)) return;

    const productItem = link.closest(CONFIG.SELECTORS.productItem);
    if (!productItem) return;

    const imgTwo = getSecondImage(link);
    if (!imgTwo) return;

    if (link.dataset.konnyThumbBound !== 'true') {
      link.dataset.konnyThumbBound = 'true';
      link.dataset.konnyThumbHover = 'false';

      link.addEventListener('pointerenter', () => {
        link.dataset.konnyThumbHover = 'true';
        syncDesiredState(link, false);
      }, { passive: true });

      productItem.addEventListener('pointerleave', () => {
        link.dataset.konnyThumbHover = 'false';
        syncDesiredState(link, true);
      }, { passive: true });
    }

    const isHover = link.dataset.konnyThumbHover === 'true';
    syncDesiredState(link, !isHover);
  }

  function scan(root) {
    if (!root || !matchedHandles || !matchedHandles.size) return;

    if (root.matches && root.matches(CONFIG.SELECTORS.productLink)) {
      bindThumbnailRule(root);
    }

    if (root.querySelectorAll) {
      root.querySelectorAll(CONFIG.SELECTORS.productLink).forEach(bindThumbnailRule);
    }
  }

  function scheduleScan(node) {
    clearTimeout(scanTimer);
    scanTimer = setTimeout(() => {
      scan(node || document);
    }, CONFIG.OBSERVER_DEBOUNCE);
  }

  function scheduleScrollScan() {
    clearTimeout(scrollScanTimer);
    scrollScanTimer = setTimeout(() => {
      scan(document);
    }, CONFIG.SCROLL_SCAN_DEBOUNCE);
  }

  function startPeriodicRescan() {
    stopPeriodicRescan();

    periodicScanTimer = setInterval(() => {
      scan(document);
    }, CONFIG.RESCAN_INTERVAL);

    periodicScanStopTimer = setTimeout(() => {
      stopPeriodicRescan();
    }, CONFIG.RESCAN_DURATION);
  }

  function stopPeriodicRescan() {
    if (periodicScanTimer) {
      clearInterval(periodicScanTimer);
      periodicScanTimer = null;
    }

    if (periodicScanStopTimer) {
      clearTimeout(periodicScanStopTimer);
      periodicScanStopTimer = null;
    }
  }

  function observeDynamicList() {
    if (typeof MutationObserver === 'undefined') return;

    const target =
      document.querySelector(CONFIG.SELECTORS.productList) ||
      document.body;

    if (!target) return;

    rootObserver = new MutationObserver((mutations) => {
      let shouldRescan = false;

      for (let i = 0; i < mutations.length; i++) {
        const mutation = mutations[i];

        if (mutation.type === 'childList') {
          if (mutation.addedNodes && mutation.addedNodes.length) {
            shouldRescan = true;
            break;
          }
        }

        if (mutation.type === 'attributes') {
          shouldRescan = true;
          break;
        }
      }

      if (shouldRescan) {
        scheduleScan(document);
      }
    });

    rootObserver.observe(target, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
  }

  function bindGlobalTriggers() {
    window.addEventListener('scroll', scheduleScrollScan, { passive: true });
    window.addEventListener('resize', scheduleScrollScan, { passive: true });
    window.addEventListener('load', () => {
      scan(document);
      startPeriodicRescan();
    }, { once: true });

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        scan(document);
      }
    });
  }

  async function init() {
    try {
      const region = getRegion();
      if (!region) {
        console.warn('[Konny Thumbnail Rules] Region could not be resolved.');
        return;
      }

      const context = getCurrentPageContext();
      if (!context.pageType || !context.category) return;

      const rules = await fetchRules(region);
      matchedHandles = getMatchingHandles(rules, context);

      if (!matchedHandles.size) return;

      scan(document);
      observeDynamicList();
      bindGlobalTriggers();
      startPeriodicRescan();
    } catch (error) {
      console.error('[Konny Thumbnail Rules]', error);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();